import { useState } from 'react'
import type { Payment } from '../lib/types'

type Props = {
  payment: Payment
  title?: string
  variant?: 'lime' | 'ink'   // lime = on red/lime block; ink = on lime page
}

function CopyChip({ value, label }: { value: string; label: string }) {
  const [copied, setCopied] = useState(false)
  async function copy() {
    try {
      await navigator.clipboard.writeText(value)
      setCopied(true)
      setTimeout(() => setCopied(false), 1500)
    } catch {
      // clipboard blocked — silently no-op, user can still select the text
    }
  }
  return (
    <button
      type="button"
      onClick={copy}
      className="inline-flex items-center gap-1 border-[2px] border-ink bg-offwhite text-ink px-2 py-1 text-xs font-display tracking-display uppercase hover:bg-lime"
      aria-label={`Kopieer ${label}`}
    >
      {copied ? 'Gekopieerd ✓' : 'Kopieer'}
    </button>
  )
}

export default function PaymentBlock({ payment, title = 'Betaling', variant = 'ink' }: Props) {
  const bg = variant === 'lime' ? 'bg-lime' : 'bg-ink text-offwhite'
  const labelColor = variant === 'lime' ? 'text-ink/60' : 'text-offwhite/60'
  const valueClass = variant === 'lime' ? 'text-ink' : 'text-offwhite'

  return (
    <div className={`border-[3px] border-ink shadow-block ${bg} p-5 sm:p-6`}>
      <div className="flex items-baseline justify-between gap-3 mb-2">
        <h3 className="font-display text-3xl tracking-display uppercase">{title}</h3>
        <span className={`font-display text-2xl tracking-display ${variant === 'lime' ? 'text-red' : 'text-lime'}`}>
          €{payment.amountEur}
        </span>
      </div>
      <p className={`text-sm mb-4 ${variant === 'lime' ? 'text-ink/80' : 'text-offwhite/80'}`}>
        {payment.aantalPlaatsen} × €{(payment.pricePerPlaceCents / 100).toFixed(2).replace('.', ',')}.
        Je plek staat definitief vast zodra de betaling binnen is.
      </p>

      <dl className="space-y-3 text-base">
        <div className="grid grid-cols-[7rem_1fr_auto] gap-3 items-center">
          <dt className={`font-display text-sm tracking-display uppercase ${labelColor}`}>IBAN</dt>
          <dd className={`font-mono text-sm sm:text-base break-all ${valueClass}`}>{payment.ibanFormatted || '—'}</dd>
          {payment.iban && <CopyChip value={payment.iban} label="IBAN" />}
        </div>
        <div className="grid grid-cols-[7rem_1fr_auto] gap-3 items-center">
          <dt className={`font-display text-sm tracking-display uppercase ${labelColor}`}>Begunstigde</dt>
          <dd className={`text-sm sm:text-base ${valueClass}`}>{payment.beneficiary}</dd>
          <span />
        </div>
        <div className="grid grid-cols-[7rem_1fr_auto] gap-3 items-center">
          <dt className={`font-display text-sm tracking-display uppercase ${labelColor}`}>Mededeling</dt>
          <dd className={`font-mono text-sm sm:text-base break-all ${valueClass}`}>
            <strong>{payment.mededeling}</strong>
          </dd>
          <CopyChip value={payment.mededeling} label="mededeling" />
        </div>
      </dl>

      {payment.qrDataUrl && (
        <div className="mt-5 flex flex-col sm:flex-row sm:items-center gap-4">
          <img
            src={payment.qrDataUrl}
            alt="QR-code voor SEPA overschrijving"
            className="border-[3px] border-ink bg-offwhite w-40 h-40 sm:w-48 sm:h-48"
          />
          <p className={`text-sm ${variant === 'lime' ? 'text-ink/80' : 'text-offwhite/80'}`}>
            Scan met je bank-app — bedrag, IBAN en mededeling worden automatisch ingevuld.
            Of typ de gegevens hierboven over.
          </p>
        </div>
      )}

      {!payment.iban && (
        <p className="mt-4 text-sm text-red">
          Betaalgegevens nog niet geconfigureerd — neem contact op via{' '}
          <a className="underline" href="mailto:rommelmarkt@fluweelfeest.be">rommelmarkt@fluweelfeest.be</a>.
        </p>
      )}
    </div>
  )
}
